import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, Edit2, Trash2, RefreshCw } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { listUserCollaborations, deleteCollaboration, UserCollaborationSummary } from "../../api/colaboracoes";
import { statusLabelPt } from "../../api/admin/status";
import AccountShell from "./AccountShell";
import { AccountRole } from "./AccountSidebar";
import TablePagination from "./TablePagination";

export default function MyRequests({ role }: { role: Exclude<AccountRole, "Admin"> }) {
  const { getUserIdFromToken, getCsrfToken } = useAuth();
  const userId = getUserIdFromToken();
  const token = getCsrfToken();
  const navigate = useNavigate();
  const [rows, setRows] = useState<UserCollaborationSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [reload, setReload] = useState(0);
  const [deleting, setDeleting] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true); setError("");
      try {
        if (!userId) throw new Error("Sessão indisponível.");
        // Collect the user's own metadata before filtering, never filter a single server page.
        const first = await listUserCollaborations(userId, token, 1, 100);
        const items = [...first.items];
        for (let number = 2; number <= first.totalPages; number++) {
          if (cancelled) return;
          items.push(...(await listUserCollaborations(userId, token, number, 100)).items);
        }
        items.sort((a, b) => new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime());
        if (!cancelled) setRows(items);
      } catch {
        if (!cancelled) { setRows([]); setError("Não foi possível carregar suas solicitações. Tente atualizar a lista."); }
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [userId, token, reload]);

  const filtered = useMemo(() => rows.filter(row => (!status || row.status === status) &&
    `${row.colaboracao_id} ${row.nome_especie}`.toLocaleLowerCase().includes(search.trim().toLocaleLowerCase())), [rows, status, search]);
  const pages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const current = Math.min(page, pages);
  const visible = filtered.slice((current - 1) * pageSize, current * pageSize);
  const suffix = role === "Colaborador" ? "colaborador" : "usuario-comum";
  const remove = async (row: UserCollaborationSummary) => {
    if (!window.confirm(`Excluir a solicitação #${row.colaboracao_id} e suas imagens?`)) return;
    setDeleting(row.colaboracao_id); setError("");
    try {
      await deleteCollaboration(row.colaboracao_id, token || "");
      setReload(value => value + 1);
    } catch { setError("Não foi possível excluir a solicitação."); }
    finally { setDeleting(null); }
  };

  return <AccountShell role={role} activeItem={role === "Comum" ? "minhas-colaboracoes" : "solicitacoes"} title="Minhas solicitações">
    <section className="account-table-panel" aria-label="Minhas solicitações">
      <div className="account-table-toolbar">
        <label>Buscar solicitação<input className="form-control" placeholder="Espécie ou número" value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} /></label>
        <label>Status<select className="form-select" value={status} onChange={e => { setStatus(e.target.value); setPage(1); }}>
          <option value="">Todos</option>{Object.entries(statusLabelPt).map(([key, label]) => <option key={key} value={key}>{label}</option>)}
        </select></label>
        <button type="button" className="btn btn-outline-secondary" aria-label="Atualizar solicitações" disabled={loading} onClick={() => setReload(value => value + 1)}><RefreshCw size={18} /></button>
      </div>
      {error && <div className="alert alert-danger" role="alert">{error}</div>}
      <div className="table-responsive" tabIndex={0} role="region" aria-label="Tabela de solicitações">
        <table className="table align-middle"><thead><tr><th scope="col">#</th><th scope="col">Espécie</th><th scope="col">Criada em</th><th scope="col">Status</th><th scope="col">Ações</th></tr></thead>
          <tbody>{loading ? <tr><td colSpan={5} role="status">Carregando solicitações…</td></tr> : !visible.length ? <tr><td colSpan={5}>Nenhuma solicitação encontrada.</td></tr> : visible.map(row => <tr key={row.colaboracao_id}>
            <td>{row.colaboracao_id}</td><td>{row.nome_especie}</td><td>{row.created_at ? new Date(row.created_at).toLocaleDateString("pt-BR") : "—"}</td>
            <td><span className={`account-status is-${row.status}`}>{statusLabelPt[row.status] || row.status}</span></td>
            <td><div className="account-row-actions">
              <button type="button" className="btn btn-icon" aria-label={`Visualizar solicitação ${row.colaboracao_id}`} title="Visualizar" onClick={() => navigate(`/colaboracao-${suffix}/${row.colaboracao_id}`)}><Eye size={18} /></button>
              {role === "Colaborador" && row.status === "correction" && <button type="button" className="btn btn-icon" aria-label={`Editar solicitação ${row.colaboracao_id}`} title="Editar" onClick={() => navigate(`/colaboracao-${suffix}/${row.colaboracao_id}?editar=1`)}><Edit2 size={18} /></button>}
              {["pending", "rejected"].includes(row.status) && <button type="button" className="btn btn-icon text-danger" disabled={!!deleting} aria-label={`Excluir solicitação ${row.colaboracao_id}`} title="Excluir" onClick={() => remove(row)}><Trash2 size={18} /></button>}
            </div></td>
          </tr>)}</tbody>
        </table>
      </div>
      <TablePagination page={current} totalPages={pages} pageSize={pageSize} total={filtered.length} disabled={loading}
        onPageChange={setPage} onPageSizeChange={size => { setPageSize(size); setPage(1); }} />
    </section>
  </AccountShell>;
}
